import IMask from "imask";
import { createElement } from "commonAssets/lib/createElement";

/**
 * Web Component for Social Security Number Input with Auto-Masking
 *
 * Usage:
 * <ssn-input
 *   name="ssn"
 *   required
 *   disabled
 *   data-label="Social Security Number"
 *   data-validate="ssn"
 * >
 * </ssn-input>
 */
class SsnInput extends HTMLElement {
    /** @type {InputMask} */
    #mask;
    #isMaskComplete = false;

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
        // Load inline styles
        this.shadowRoot.appendChild(
            createElement(
                "style",
                {
                    type: "text/css",
                },
                [],
                `
                :host {
                    /* Allow UA to adapt native form controls to color scheme */
                    color-scheme: light dark;
                    display: block;
                    position: relative;
                }
                
                .ssn-container {
                    position: relative;
                    display: flex;
                    align-items: center;
                    border: 1px solid var(--bs-gray-600, #dee2e6);
                    border-radius: 0.375rem;
                    background-color: var(--bs-body-bg, #ffffff);
                    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                }
                
                .ssn-container:focus-within {
                    border-color: var(--bs-primary, #0d6efd);
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
                }
                
                .ssn-container.is-invalid {
                    border-color: var(--bs-danger, #dc3545);
                }
                
                .ssn-container.is-invalid:focus-within {
                    border-color: var(--bs-danger, #dc3545);
                    box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
                }
                
                input {
                    background-color: transparent;
                    border: none;
                    outline: none;
                    box-shadow: none;
                    width: 100%;
                    display: block;
                    padding: 0.375rem 0.75rem;
                    font-size: 1rem;
                    font-weight: 400;
                    line-height: 1.5;
                    color: var(--bs-body-color, #212529);
                    caret-color: var(--bs-body-color, #212529);
                    letter-spacing: 0.1em;
                }
                
                input:disabled {
                    background-color: var(--bs-secondary-bg, #e9ecef);
                    opacity: 0.65;
                }
            `
            )
        );

        // Create the container
        const container = createElement("div", { class: "ssn-container" });

        // Create the input element
        const input = createElement("input", {
            type: "text",
        });

        // Initialize imask
        const maskOptions = {
            mask: "XXX-XX-0000",
            definitions: {
                X: { mask: "0", displayChar: "X", placeholderChar: "#" },
            },
            lazy: false,
            overwrite: "shift",
        };
        this.#mask = IMask(input, maskOptions);
        this.#mask.on("complete", () => {
            this.#isMaskComplete = true;
        });

        // Append the input to the container
        container.appendChild(input);

        // Append the container to the Shadow DOM
        this.shadowRoot.appendChild(container);
    }

    disconnectedCallback() {
        if (this.#mask) {
            this.#mask.off("complete");
            this.#mask.destroy();
        }
        this.#isMaskComplete = false;
        this.shadowRoot.innerHTML = "";
    }

    /**
     * Get the current value (unmasked)
     * @returns {string} The current SSN value
     */
    get value() {
        return this.#mask?.unmaskedValue;
    }

    /**
     * Set the current value
     * @param {string} value - The SSN value to set
     */
    set value(value) {
        if (this.#mask) {
            this.#mask.unmaskedValue = value ?? "";
            this.#isMaskComplete = true;
        }
    }

    /**
     * Get the name attribute
     * @returns {string} The name attribute
     */
    get name() {
        return this.getAttribute("name") || "";
    }

    /**
     * Check if the current SSN is valid
     * @returns {boolean} Whether the SSN is valid
     */
    isValid() {
        const value = this.value;

        // If no value, check if field is required
        if (!value || value.trim() === "") {
            return !this.hasAttribute("required");
        }
        return this.#isMaskComplete;
    }

    /**
     * Validate the SSN input
     * @returns {boolean} Whether the field is valid
     */
    validate() {
        // Clear any existing errors first
        this.clearError();

        // Check SSN validation
        if (!this.isValid()) {
            this.showError("Invalid Social Security Number");
            return false;
        }

        return true;
    }

    /**
     * Show error message for the SSN input
     * @param {string} message - The error message to display
     */
    showError(message) {
        // Find the parent element that should contain the error
        const parentElement = this.parentElement;
        if (!parentElement) return;

        // Add error class to container
        const container = this.shadowRoot.querySelector(".ssn-container");
        if (container) {
            container.classList.add("is-invalid");
        }

        // Look for existing error span or create one
        let errorSpan = parentElement.querySelector(".error");
        if (!errorSpan) {
            // Look for a label that might contain an error span
            const label = parentElement.querySelector(
                `label[for="${this.name}"]`
            );
            if (label) {
                errorSpan = label.querySelector(".error");
                if (!errorSpan) {
                    errorSpan = document.createElement("span");
                    errorSpan.className = "error";
                    label.appendChild(errorSpan);
                }
            } else {
                // Create error span as sibling
                errorSpan = document.createElement("span");
                errorSpan.className = "error";
                parentElement.appendChild(errorSpan);
            }
        }

        // Set error message
        errorSpan.textContent = message;
    }

    /**
     * Clear error message for the SSN input
     */
    clearError() {
        // Remove error class from container
        const container = this.shadowRoot.querySelector(".ssn-container");
        if (container) {
            container.classList.remove("is-invalid");
        }

        // Find and clear error message
        const parentElement = this.parentElement;
        if (!parentElement) return;

        // Look for error span in label or parent
        const label = parentElement.querySelector(`label[for="${this.name}"]`);
        const errorSpan =
            label?.querySelector(".error") ||
            parentElement.querySelector(".error");

        if (errorSpan) {
            errorSpan.textContent = "";
        }
    }
}

// Register the web component
if (!customElements.get("ssn-input")) {
    customElements.define("ssn-input", SsnInput);
}

export default SsnInput;
